export const OPPORTUNITY_STAGES = [
  'interested',
  'preparing',
  'ready_to_apply',
  'applied',
  'recruiter_screen',
  'interviewing',
  'final_round',
  'offer',
  'accepted',
] as const;

export const TERMINAL_OPPORTUNITY_STAGES = [
  'rejected',
  'withdrawn',
  'declined',
  'expired',
  'archived',
] as const;

export type OpportunityStage =
  | (typeof OPPORTUNITY_STAGES)[number]
  | (typeof TERMINAL_OPPORTUNITY_STAGES)[number];

export type RelationshipStage =
  | 'new'
  | 'active_conversation'
  | 'waiting'
  | 'follow_up'
  | 'dormant'
  | 'archived';

export type WorkState =
  | 'queued'
  | 'running'
  | 'waiting_for_user'
  | 'succeeded'
  | 'partial'
  | 'failed_retryable'
  | 'failed_terminal';

export interface WorkOperation {
  id: string;
  kind: 'prepare' | 'discovery' | 'gmail' | 'reminders' | 'intelligence';
  state: 'queued' | 'running' | 'succeeded' | 'failed_retryable' | 'failed_terminal';
  relatedEntityType?: string;
  relatedEntityId?: string;
  resultState?: string;
  errorCode?: string;
  message?: string;
  queuedAt: string;
  startedAt?: string;
  completedAt?: string;
  updatedAt: string;
}

export interface CandidateSession {
  uid: string;
  email: string;
  displayName?: string;
  accessKind: 'direct' | 'inherited_admin';
}

export interface OnboardingState {
  betaNoticeAcceptedAt?: string;
  resumeDocumentId?: string;
  elicitationRound: 0 | 1 | 2;
  profileReady: boolean;
  reducedQualityMode: boolean;
  completedSteps: string[];
}

export interface OnboardingAnswerDraft {
  round: 1 | 2;
  answers: Record<string, string>;
  updatedAt?: string;
}

export interface IntegrationSourceStatus {
  state: 'connected' | 'disconnected' | 'stale' | 'blocked' | 'error';
  lastSuccessfulAt?: string;
  lastAttemptAt?: string;
  message?: string;
  code?: string;
}

export interface IntegrationStatus {
  provider: 'gmail' | 'calendar' | 'apify' | 'openai' | 'extension';
  state: 'connected' | 'disconnected' | 'stale' | 'blocked' | 'error' | 'revoked' | 'reconnect_required';
  lastSuccessfulAt?: string;
  lastAttemptAt?: string;
  message?: string;
  authorizationExpiresAt?: string;
  authorizationExpiredAt?: string;
  reconnectRequired?: boolean;
  labelStandingRuleApprovedAt?: string;
  sourceStates?: Partial<Record<'linkedin' | 'indeed', IntegrationSourceStatus>>;
}

export type CapabilityName = 'ai' | 'discovery' | 'gmail' | 'calendar' | 'notifications' | 'chromeCompanion';

export interface CapabilityStatus {
  available: boolean;
  usable: boolean;
  code: 'available' | 'provider_unconfigured' | 'candidate_action_required';
  message: string;
  actionRoute: string;
}

export interface NotificationConsent {
  state: 'disabled' | 'enabled';
  selectedEmail?: string;
  consentedAt?: string;
  disabledAt?: string;
}

export interface ExtensionGrantResponse {
  grant: string;
  expiresAt: string;
}

export interface ManualRecoveryAnswer {
  fieldId: string;
  label: string;
  value: string;
}

export interface ManualRecoveryFile {
  fieldId: string;
  label: string;
  filename: string;
  downloadUrl?: string;
  provenanceRefs: string[];
}

export interface ManualRecoveryPackage {
  schemaVersion: 1;
  directUrl: string;
  reason: string;
  answers: ManualRecoveryAnswer[];
  files: ManualRecoveryFile[];
  steps: string[];
}

export interface ManualRecoveryResponse {
  sessionId: string;
  status: 'manual_recovery';
  createdAt: string;
  updatedAt: string;
  manualPackage: ManualRecoveryPackage;
}

export interface BootstrapPayload {
  user: CandidateSession;
  onboarding: OnboardingState;
  notifications: NotificationConsent;
  capabilities: Record<CapabilityName, CapabilityStatus>;
  integrations: IntegrationStatus[];
  counts: {
    opportunities: number;
    actionItems: number;
    discoveryResults: number;
    relationships: number;
    documents: number;
  };
  homeSummary?: {
    approvals: number;
    missingInformation: number;
    interviews: number;
    overdueCommitments: number;
    staleOpportunities: number;
    discoveryAwaitingDecision: number;
  };
  generatedAt: string;
}

export interface Opportunity {
  id: string;
  companyName: string;
  title: string;
  stage: OpportunityStage;
  allowedStageTransitions: OpportunityStage[];
  location?: string;
  locations?: string[];
  compensation?: string;
  fitScore?: number;
  fitSummary?: string;
  source?: 'linkedin' | 'indeed' | 'manual';
  sourceUrl?: string;
  lastActivityAt: string;
  nextAction?: string;
  contactNames?: string[];
  blockers?: string[];
  updatedAt: string;
}

export interface ActionItem {
  id: string;
  kind:
    | 'approval'
    | 'missing_information'
    | 'interview'
    | 'commitment'
    | 'stale_opportunity'
    | 'integration_incident'
    | 'discovery_review'
    | 'manual_recovery';
  title: string;
  detail: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  state: 'open' | 'snoozed' | 'completed' | 'dismissed';
  dueAt?: string;
  snoozedUntil?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  resolutionTarget?: {
    route: string;
    focus: string;
    evidenceCode: string;
    returnTo: string;
  };
  candidateContactIds?: string[];
  candidateContactSummaries?: string[];
  suggestion?: {
    kind: 'location_strategy';
    decision: 'interesting' | 'dismissed';
    value: string;
    requiresExplicitConfirmation: true;
  };
  behaviorEvidenceResultIds?: string[];
  createdAt: string;
}

export interface Contact {
  id: string;
  name: string;
  emails: string[];
  phone?: string;
  title?: string;
  companyIds?: string[];
  companyNames?: string[];
  relationshipIds?: string[];
  opportunityIds?: string[];
  source?: 'user' | 'gmail' | 'application';
  updatedAt: string;
}

export type ActionReviewDecision =
  | {
      kind: 'contact_merge';
      decision: 'approve';
      canonicalContactId: string;
      canonical: {name: string; emails: string[]; phone: string; title: string};
    }
  | {kind: 'contact_merge'; decision: 'reject'}
  | {kind: 'preference'; decision: 'approve'; personaId: string}
  | {kind: 'preference'; decision: 'edit'; personaId: string; value: string}
  | {kind: 'preference'; decision: 'reject'};

export interface DiscoveryResult {
  id: string;
  source: 'linkedin' | 'indeed' | 'manual';
  sourceId: string;
  canonicalUrl: string;
  companyName: string;
  title: string;
  location?: string;
  compensation?: string;
  fitScore: number;
  fitSummary: string;
  eligibility: 'eligible' | 'ineligible' | 'uncertain';
  state: 'new' | 'saved' | 'interesting' | 'dismissed' | 'converted' | 'expired';
  discoveredAt: string;
  expiresAt?: string;
}

export interface DiscoveryQuestionAnswer {
  answer: string;
  evidence: Array<{label: string; value: string}>;
  uncertainty: string[];
  sourceUrl: string;
  grounded: true;
}

export interface Relationship {
  id: string;
  name: string;
  stage: RelationshipStage;
  emails: string[];
  phone?: string;
  agency?: string;
  companyNames?: string[];
  lastInteractionAt?: string;
  nextAction?: string;
  opportunityCount: number;
  updatedAt: string;
}

export interface CareerFact {
  id: string;
  type: string;
  value: string;
  state: 'source_backed' | 'user_confirmed' | 'inferred_unconfirmed' | 'rejected';
  confidence: number;
  sourceLabel: string;
  provenance: string[];
  archived: boolean;
  reviewRequired?: boolean;
  reviewReason?: string;
  updatedAt: string;
}

export interface CareerEvidence {
  id: string;
  kind: 'text' | 'link' | 'document' | 'voice';
  label: string;
  state: 'source_backed' | 'transcript_review';
  transcript?: string;
  sourceUrl?: string;
  createdAt?: string;
}

export interface ApprovedBullet {
  id: string;
  text: string;
  state: 'draft' | 'approved' | 'rejected' | 'archived';
  tags: string[];
  factIds: string[];
  personaIds?: string[];
  replacesBulletId?: string;
  version: number;
  updatedAt: string;
}

export interface Persona {
  id: string;
  name: string;
  roleFamily: string;
  seniority?: string;
  locationStrategy?: string;
  compensationMinimum?: number;
  confirmed: boolean;
  queries: string[];
  active?: boolean;
  queryConfirmation?: 'draft' | 'confirmed';
  queriesConfirmedAt?: string;
  updatedAt: string;
}

export interface PersonaQuerySuggestions {
  queries: string[];
  basis: Array<{
    kind: 'role' | 'seniority' | 'location' | 'compensation' | 'experience' | 'technology' | 'interest';
    label: string;
    sourceFactId?: string;
  }>;
  requiresConfirmation: true;
}

export interface ApplicationAnswer {
  id: string;
  normalizedQuestion: string;
  originalQuestion: string;
  answer: string;
  sourceApplicationId?: string;
  sourcePageId?: string;
  sensitivity: 'none' | 'demographic' | 'disability' | 'veteran' | 'criminal_history' | 'work_authorization' | 'attestation' | 'signature' | 'employer_consent';
  provenance: 'direct_user' | 'imported' | 'inferred' | 'ai_proposed';
  reusePreference: 'always_propose' | 'ask_each_time' | 'never';
  updatedAt: string;
}

export interface DocumentArtifact {
  id: string;
  kind: 'source_resume' | 'tailored_resume' | 'cover_letter' | 'supporting' | 'recording' | 'career_evidence' | 'email_attachment' | 'export';
  name: string;
  mimeType: string;
  sizeBytes?: number;
  state: WorkState | 'draft' | 'approved' | 'submitted' | 'review_required';
  version: number;
  currentVersionId?: string;
  currentVersionSha256?: string;
  approvedAt?: string;
  downloadUrl?: string;
  previewUrl?: string;
  reviewRequired?: boolean;
  reviewReason?: string;
  createdAt: string;
}

export interface CoverLetterFactProvenance {
  factId: string;
  factVersion: number;
  sourceRefs: string[];
}

export interface CoverLetterGenerationResult {
  state: 'waiting_for_user' | 'approved';
  documentId: string;
  versionId: string;
  provenance: Array<{
    bulletId: string;
    bulletVersion: number;
    exactText: string;
    factIds: string[];
    facts: CoverLetterFactProvenance[];
  }>;
  conservativeRenderer: true;
  onePage: true;
  paragraphCount: 3 | 4;
  reused: boolean;
}

export interface ElicitationQuestion {
  id: string;
  prompt: string;
  rationale?: string;
  answer?: string;
}

export interface PageResult<T> {
  items: T[];
  nextCursor?: string;
}

export const humanizeStage = (stage: string): string =>
  stage
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
